import { DEFAULT_SIZES } from '~/lib/constants';
import { analyzeImage } from '~/utils/imageAnalysis';
import { getCollectionAndName } from '~/utils/collection';
import { fileUtils } from '~/utils/file';

interface Dimensions {
  width: number;
  height: number;
}

interface ImageData {
  id: string;
  file: File;
  preview: string;
  collection: string;
  name: string;
  originalWidth: number;
  originalHeight: number;
  fileMeta: {
    analysis: any;
    preview: string;
    raw: {
      name: string;
      lastModified: number;
      lastModifiedDate: string;
      size: number;
      type: string;
      path: string;
      webkitRelativePath: string;
      fullPath: string;
      parsedPath: {
        collection: string;
        originalName: string;
        nameWithoutExtension: string;
        extension: string | undefined;
      };
      dimensions: {
        width: number;
        height: number;
        aspectRatio: number;
      };
      uploadTimestamp: number;
      uploadDate: string;
      fileExtension?: string;
      mimeType: string;
      sizeInMB: string;
      error?: string;
    };
    originalWidth: number;
    originalHeight: number;
    fileType: string;
    fileName: string;
    fileSize: number;
    uploadedAt: string;
    error?: string;
  };
  printMeta: {
    sellOriginal: boolean;
    originalPrice: string;
    sellPrints: boolean;
    selectedPrintSizes: string[];
  };
}

function getMatchingPrintSizes(dimensions: Dimensions) {
  const aspectRatio = dimensions.width / dimensions.height;
  const aspectRatioTolerance = 0.02;

  return DEFAULT_SIZES.filter((size) => {
    const sizeAspectRatio = size.width / size.height;
    return Math.abs(sizeAspectRatio - aspectRatio) <= aspectRatioTolerance;
  }).map((size) => `${size.width}x${size.height}`);
}

async function getImageDimensions(file: File): Promise<Dimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function createBaseImageData(file: File, id?: string): Promise<Partial<ImageData>> {
  const { collection, name } = getCollectionAndName(file);
  let dimensions = { width: 0, height: 0 };
  let preview = '';
  let processedFile = file;

  // Check if file is a TIFF and convert to JPEG
  const isTiff = file.type === 'image/tiff' || file.type === 'image/x-tiff' || /\.tiff?$/i.test(file.name);

  if (isTiff) {
    const convertedFile = await fileUtils.convertTiffToJpeg(file);
    if (convertedFile) {
      processedFile = convertedFile;
    } else {
      throw new Error('Failed to convert TIFF file');
    }
  }

  try {
    dimensions = await getImageDimensions(processedFile);
    preview = URL.createObjectURL(processedFile);
  } catch (error) {
    console.error('Failed to process image:', error);
  }

  const analysis = analyzeImage(processedFile, dimensions);
  const selectedPrintSizes = getMatchingPrintSizes(dimensions);

  return {
    id: id || crypto.randomUUID(),
    file: processedFile,
    collection,
    name,
    originalWidth: dimensions.width,
    originalHeight: dimensions.height,
    fileMeta: {
      analysis,
      preview,
      originalWidth: dimensions.width,
      originalHeight: dimensions.height,
      fileType: processedFile.type,
      fileName: processedFile.name,
      fileSize: processedFile.size,
      uploadedAt: new Date().toISOString(),
      raw: {
        name: processedFile.name,
        lastModified: processedFile.lastModified,
        lastModifiedDate: new Date(processedFile.lastModified).toISOString(),
        size: processedFile.size,
        type: processedFile.type,
        path: processedFile.webkitRelativePath || (processedFile as any).path || '',
        webkitRelativePath: processedFile.webkitRelativePath || '',
        fullPath: (processedFile as any).path || '',
        parsedPath: {
          collection,
          originalName: processedFile.name,
          nameWithoutExtension: name,
          extension: processedFile.name.split('.').pop()?.toLowerCase(),
        },
        dimensions: {
          width: dimensions.width,
          height: dimensions.height,
          aspectRatio: dimensions.width / dimensions.height,
        },
        uploadTimestamp: Date.now(),
        uploadDate: new Date().toISOString(),
        fileExtension: processedFile.name.split('.').pop()?.toLowerCase(),
        mimeType: processedFile.type,
        sizeInMB: (processedFile.size / (1024 * 1024)).toFixed(2),
      },
    },
    printMeta: {
      sellOriginal: true,
      originalPrice: '599',
      sellPrints: true,
      selectedPrintSizes,
    },
  };
}

export async function processFile(file: File, id?: string): Promise<ImageData> {
  const imageData = await createBaseImageData(file, id);
  return imageData as ImageData;
}
